/* eslint-disable react-hooks/exhaustive-deps */
import { nzApiClient } from '@nizza/core';
import { useContext, useEffect, useState } from 'react';
import baseLogger from '~logger';
import { Answer, Question } from '../../lib/types';
import { ActionsContext, SettingContext } from '../context';
import { useControlsContext } from '../context/ControlsContext';
import useUpdateVotes from './useUpdateVotes';

const logger = baseLogger.child('UseQuestion');

const QUESTION_TIME = 20000;

const useQuestion = () => {
  const {
    setting: { account, idLivestreaming },
  } = useContext(ActionsContext);
  const { infoSocket } = useContext(SettingContext);
  const {
    setQuestionTime: setShowQuestionButtonTime,
    hideQuestion,
    setHideQuestion,
  } = useControlsContext();
  const { question, setQuestion, socket } = infoSocket || {};
  const { saveUpdateVotes } = useUpdateVotes({ socket });

  //states
  const [currentQuestion, setCurrentQuestion] = useState<Question>();
  //const [hideQuestion, setHideQuestion] = useState<boolean>(false); //Deprecated in favor of useControlsContext.
  const [timeExpired, setTimeExpired] = useState(false);
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState<number | null>(
    null,
  );
  const [timerCountDown, setTimerCountDown] = useState<number | null>(null);
  const [questionCloseTimeOut, setQuestionCloseTimeOut] =
    useState<NodeJS.Timeout | null>(null);
  const [timeOutFetchResults, setTimeOutFetchResults] =
    useState<NodeJS.Timeout | null>(null);
  const [timeOutHideQuestion, setTimeOutHideQuestion] =
    useState<NodeJS.Timeout | null>(null);
  const [selectedAnswersIndices, setSelectedAnswersIndices] = useState<
    Array<number>
  >([]);
  const [dataResults, setDataResults] = useState<Answer[] | null>(null);

  //handlers
  const handlePressAnswer = (indexAnswerValue: number) => {
    const isAlreadySelected = selectedAnswersIndices.includes(indexAnswerValue);

    if (currentQuestion?.type === 'quiz') {
      const selectedAnswer = isAlreadySelected ? [] : [indexAnswerValue];
      if (selectedAnswersIndices.length === 0) {
        setSelectedAnswersIndices(selectedAnswer);
        const timeOut = setTimeout(() => {
          setHideQuestion(true);
        }, 4000);
        setQuestionCloseTimeOut(timeOut);
      }
    }

    if (currentQuestion?.type === 'poll') {
      !isAlreadySelected &&
        setSelectedAnswersIndices([
          ...selectedAnswersIndices,
          indexAnswerValue,
        ]);
    }
  };

  //methods
  const resetData = () => {
    setDataResults(null);
    setSelectedAnswersIndices([]);
    timeOutFetchResults && clearTimeout(timeOutFetchResults);
    timeOutHideQuestion && clearTimeout(timeOutHideQuestion);
    setHideQuestion(true);
  };

  const sendAnswers = async () => {
    await saveUpdateVotes(
      idLivestreaming,
      currentQuestion?.index ?? 0,
      selectedAnswersIndices,
    );
  };

  const clearQuestion = () => {
    setTimeExpired(true);
    setHideQuestion(true);
    setQuestion && setQuestion(undefined);
    setShowQuestionButtonTime && setShowQuestionButtonTime(0);
    setCurrentQuestion(undefined);
  };

  const fetchResults = () => {
    const getData = async () => {
      const response = await nzApiClient.rest<{ question: Question }>({
        path: `/v1/live-admin/questions/${idLivestreaming}/${
          currentQuestion?.index || 0
        }`,
        queryParams: {
          account: account,
        },
      });
      setDataResults((response.question as Question).answers ?? null);
    };

    getData()
      .catch(err => {
        logger.error(err);
      })
      .finally(() => {
        setHideQuestion(false);
        const timeOut = setTimeout(() => {
          clearQuestion();
        }, QUESTION_TIME);
        setTimeOutHideQuestion(timeOut);
      });
  };

  //effects
  useEffect(() => {
    resetData();
    setCurrentQuestion(question);
    if (question !== undefined) {
      setHideQuestion(false);
    }
  }, [question]);

  useEffect(() => {
    if (!currentQuestion?.time) return;
    timerCountDown && clearInterval(timerCountDown);
    currentQuestion?.time && setCurrentTimeSeconds(currentQuestion?.time);
    const dateStart = Math.floor((currentQuestion.date ?? 0) / 1000);

    const interval = window.setInterval(() => {
      const dateNow = Math.floor(Date.now() / 1000);
      const currentTimeCalc =
        (currentQuestion?.time ?? 0) - (dateNow - dateStart);
      if (currentTimeCalc < 0) {
        clearQuestion();
        return;
      }
      setCurrentTimeSeconds(currentTimeCalc);
    }, 1000);
    setTimerCountDown(interval);

    return () => {
      clearInterval(interval);
    };
  }, [currentQuestion]);

  const sendAnswerAsync = async () => {
    await sendAnswers();
    const timeOut = setTimeout(() => {
      fetchResults();
    }, 1500);
    setTimeOutFetchResults(timeOut);
  };

  useEffect(() => {
    if (questionCloseTimeOut) {
      clearTimeout(questionCloseTimeOut);
      setQuestionCloseTimeOut(null);
    }
  }, [hideQuestion]);

  useEffect(() => {
    const timeIsNotNull = typeof currentTimeSeconds == 'number';

    if (timeIsNotNull && currentTimeSeconds === 0) {
      timerCountDown && clearInterval(timerCountDown);
      setTimeExpired(true);
      sendAnswerAsync();
    } else {
      setTimeExpired(false);
    }

    setShowQuestionButtonTime &&
      setShowQuestionButtonTime(currentTimeSeconds ?? 0);
  }, [currentTimeSeconds]);

  return {
    hideQuestion,
    setHideQuestion,
    currentQuestion,
    sendAnswers,
    selectedAnswersIndices,
    handlePressAnswer,
    timeExpired,
    setTimeExpired,
    currentTimeSeconds: currentTimeSeconds ?? 0,
    dataResults,
  };
};

export default useQuestion;
